<template>
  <div v-if="loggedIn">
    {{ errorContent }}
    <!-- Summary Statistics -->
    <b-row class="match-height mb-3">
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="warning"
          icon="InboxIcon"
          :statistic="inquiryCount"
          statistic-title="Anfragen Gesamt"
        />
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="info"
          icon="ClockIcon"
          :statistic="unqualifiedCount"
          statistic-title="Zu Qualifizieren"
        />
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="success"
          icon="TrendingUpIcon"
          :statistic="`${conversionRate}%`"
          statistic-title="Konversionsrate"
        />
      </b-col>
      <b-col
        xl="3"
        md="6"
        sm="6"
      >
        <statistic-card-vertical
          color="primary"
          icon="ActivityIcon"
          :statistic="averageResponseTime"
          statistic-title="Ø Antwortzeit"
        />
      </b-col>
    </b-row>
    <b-card no-body>
      <!-- Header mit Per-Page, Suche und Tabs -->
      <div class="m-2">
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mr-1">Zeige</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-1"
              style="min-width: 80px; max-width: 100px;"
            />
            <label class="ml-1">Einträge</label>
          </b-col>
          <!-- Search und Testanfrage -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Suche..."
                type="search"
              />
              <b-button
                variant="primary"
                @click="createTestInquiry()"
              >
                <span class="text-nowrap">Testanfrage generieren</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
        <!-- Tabbar für Statusfilter -->
        <b-tabs
          v-model="currentTab"
          class="mt-2 custom-tabs"
        >
          <b-tab :title="`Qualifizieren (${allCount})`" />
          <b-tab :title="`Akzeptiert (${openCount})`" />
          <b-tab :title="`Abgelehnt (${offerCount})`" />
          <b-tab :title="`Unbekannt (${rejectedCount})`" />
          <b-tab :title="`Sonstiges (${unknownCount})`" />
        </b-tabs>
      </div>

      <!-- Tabelle -->
      <b-table
        ref="refListTable"
        striped
        hover
        :busy="loading"
        :current-page="currentPage"
        :per-page="perPage"
        :items="filteredInquiries"
        :fields="fields"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive
        show-empty
        empty-text="Keine Einträge gefunden"
        @row-clicked="onRowClicked"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-2" />
            <strong>Lade Anfragedaten...</strong>
          </div>
        </template>
        <template #cell(acceptInquiry)=" { item } ">
          <b-button
            :disabled="item.offers.length > 0"
            :variant="getButtonVariant(item)"
            @click="showQualificationModal(item)"
          >
            <feather-icon
              icon="AlignJustifyIcon"
              class="mr-50"
            />
            <span class="align-middle">
              {{ item.qualification ? item.qualification : 'Qualifizieren' }}
            </span>
          </b-button>
        </template>

        <template #cell(status)=" { item } ">
          <div>
            <b-badge :variant="getStatusVariant(item)">
              {{ getInquiryStatus(item) }}
            </b-badge>
          </div>
        </template>

        <template #cell(storageLocation)=" { item } ">
          <div>
            {{ locationById(item.storageLocation) ? locationById(item.storageLocation).name : '—' }}
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              :to="{ name: 'inquiries-view', params: { id: data.item.id } }"
            >
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Öffnen</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.qualification === 'Abgelehnt'"
              @click="deleteWarning(data)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Löschen</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <!-- Pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Zeige {{ dataMeta.from }} bis {{ dataMeta.to }} von {{ getTotalRows }} Einträgen
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="getTotalRows"
              :per-page="perPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <InquiriesQualificationModal
      ref="modal-qualifaction"
      :selected-option="selectedOption"
      :qualification-options="qualificationOptions"
      :selected-inquiry="selectedInquiry"
    />
  </div>
  <div v-else>
    Forbidden
  </div>
</template>

<script>
import Inquiry from '@/store/inquiry/Inquiry'
import vSelect from 'vue-select'

import {
  BCard,
  BTable,
  BRow,
  BCol,
  BFormCheckbox,
  BDropdown,
  BDropdownItem,
  BPagination,
  VBModal,
  BModal,
  VBToggle,
  BDropdownDivider,
  BFormSelect,
  BButton,
  BFormInput,
  BBadge,
  BTabs,
  BTab,
  BSpinner
} from 'bootstrap-vue'
import inquiryMetrics from '@/helper/inquiryMetrics'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import InquiriesQualificationModal from '../modal/InquiriesQualificationModal.vue'
import tableHelper from '../../Table+Helper'

export default {
  setup() {
    const {
      getButtonVariant,
      getStatusVariant,
      getInquiryStatus,
      getInquiryCount,
      getUnqualifiedCount,
      getAcceptedCount,
      getRejectedCount,
      getUnknownCount,
      getOtherCount,
      getInquiryToOfferConversionRate,
      getAverageResponseTime,
      generateTestInquiry
    } = inquiryMetrics()
    
    const {
      perPageOptions,
      perPage,
      filter,
      currentPage,
      refListTable,
      dataMeta,
      refetchData,
    } = tableHelper()

    return {
      getButtonVariant,
      getStatusVariant,
      getInquiryStatus,
      getInquiryCount,
      getUnqualifiedCount,
      getAcceptedCount,
      getRejectedCount,
      getUnknownCount,
      getOtherCount,
      getInquiryToOfferConversionRate,
      getAverageResponseTime,
      generateTestInquiry,
      perPageOptions,
      perPage,
      filter,
      currentPage,
      refListTable,
      dataMeta,
      refetchData,
    }
  },
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    BPagination,
    VBModal,
    BModal,
    VBToggle,
    Inquiry,
    vSelect,
    BDropdownDivider,
    BFormSelect,
    BButton,
    BFormInput,
    BBadge,
    BSpinner,
    StatisticCardVertical,
    ToastificationContent,
    InquiriesQualificationModal,
    BTabs,
    BTab,
  },
  directives: {
    'b-toggle': VBToggle,
    'b-modal': VBModal,
  },
  data() {
    return {
      sortBy: 'id',
      sortDesc: true,
      // currentTab: 0 => Alle, 1 => Offen, 2 => Angebot, 3 => Abgelehnt, 4 => Unklar
      currentTab: 0,
      loading: false,
      errorContent: '',
      qualificationOptions: Inquiry.qualificationOptions,
      inquiry: Inquiry,
      selectedInquiry: {},
      selectedOption: 'null',
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'acceptInquiry', label: 'Bewertung', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'firstName', label: 'Vorname', sortable: true },
        { key: 'lastName', label: 'Nachname', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'storageLocation', label: 'Lager Standort', sortable: true },
        { key: 'message', label: 'Nachricht', sortable: true },
        { key: 'actions', label: 'Aktionen' },
      ],
    }
  },

  computed: {
    // Alle Anfragen aus dem Store
    inquiriesEntries() {
      const inquiries = this.$store.getters.inquiriesAllEntries || []
      return [...inquiries].sort((a, b) => {
        const dateA = new Date(a.created_at)
        const dateB = new Date(b.created_at)
        return dateB.getTime() - dateA.getTime() // Newest first
      })
    },
    loggedIn() {
      return this.$store.state.authentication.status.loggedIn
    },
    // Filtert die Anfragen basierend auf dem aktuellen Tab
    filteredInquiries() {
      if (this.currentTab === 0) {
        return this.inquiriesEntries.filter(item => item.qualification === null)
      }
      if (this.currentTab === 1) {
        return this.inquiriesEntries.filter(item => item.qualification === 'Akzeptiert')
      }
      if (this.currentTab === 2) {
        return this.inquiriesEntries.filter(item => item.qualification === 'Abgelehnt')
      }
      if (this.currentTab === 3) {
        return this.inquiriesEntries.filter(item => item.qualification === 'Unbekannt')
      }
      if (this.currentTab === 4) {
        return this.inquiriesEntries.filter(item => item.qualification === 'Sonstiges')
      }
      return this.inquiriesEntries
    },
    getTotalRows() {
      return this.filteredInquiries.length
    },
    allCount() {
      return this.getUnqualifiedCount()
    },
    openCount() {
      return this.getAcceptedCount()
    },
    offerCount() {
      return this.getRejectedCount()
    },
    rejectedCount() {
      return this.getUnknownCount()
    },
    unknownCount() {
      return this.getOtherCount()
    },
    // Statistics for cards
    inquiryCount() {
      return this.getInquiryCount()
    },
    unqualifiedCount() {
      return this.getUnqualifiedCount()
    },
    conversionRate() {
      return this.getInquiryToOfferConversionRate()
    },
    averageResponseTime() {
      return this.getAverageResponseTime()
    },
  },

  watch: {
    currentTab() {
      // Reset pagination when switching tabs
      this.currentPage = 1
    }
  },
  async mounted() {
    await this.loadInquiries()
  },
  updated() {
    this.totalRows = this.getTotalRows
  },
  methods: {
    async loadInquiries() {
      this.loading = true
      this.errorContent = ''
      try {
        await this.$store.dispatch('getInquiriesEntries')
      } catch (error) {
        console.error('Error loading inquiries:', error)
        this.errorContent = 'Fehler beim Laden der Anfragedaten. Bitte versuchen Sie es erneut.'
        this.toastNotification(
          'Anfragedaten konnten nicht geladen werden.',
          'AlertTriangleIcon',
          'danger'
        )
      } finally {
        this.loading = false
      }
    },
    locationById(id) {
      return this.$store.getters.locationById(Number(id))
    },
    onRowClicked(item) {
      this.$router.push(`/inquiries-view/${item.id}`)
    },
    accteptedInquiryChanged(item) {
      this.$store.dispatch('updateInquiry', item)
    },
    deleteWarning(data) {
      this.$swal({
        title: 'Bist du sicher?',
        text: `Die Anfrage von "${data.item.firstName} ${data.item.lastName} mit der ID ${data.item.id} wirklich löschen?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-primary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$router.go()
        }
      })
    },
    showQualificationModal(item) {
      this.selectedInquiry = item
      this.selectedOption = item.qualification
      this.$refs['modal-qualifaction'].show()
    },
    qualificationSubmitted() {
      this.selectedInquiry.qualification = this.selectedOption
      this.$store.dispatch('updateInquiry', this.selectedInquiry)
    },
    onChange(events) {
      this.selectedOption = events
    },
    async createTestInquiry() {
      const testInquiry = this.generateTestInquiry()
      try {
        await this.$store.dispatch('addInquiry', testInquiry)
        await this.loadInquiries()
        this.toastNotification(
          'Testanfrage erfolgreich erstellt.',
          'CheckIcon',
          'success'
        )
      } catch (error) {
        console.error('Error creating test inquiry:', error)
        this.toastNotification(
          'Testanfrage konnte nicht erstellt werden.',
          'AlertTriangleIcon',
          'danger'
        )
      }
    },
    toastNotification(title, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.custom-tabs {
  .nav-link {
    color: #3b3b3b;
    background-color: #f8f9fa;
    &.active {
      background-color: #fff;
      border-bottom-color: #fff;
    }
  }
  .nav-tabs {
    margin-bottom: 0px;
  }
}
</style>
